import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Send,
    ChevronLeft,
    Camera,
    FileText,
    X,
    RotateCcw,
    ImagePlus,
    Paperclip,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { useReturnChat } from "@/hooks/useReturnChat";
import { ChatMessage } from "@/types";
import AnayaIcon from "@/assets/anaya.jpg";
import ProtegaLogo from "@/assets/protegalogo.png";
import axiosInstance from "@/service/axios";
import { useAuth } from "@/context/AuthContext";
import { AppDispatch } from "@/store/store";
import {
    openReturnConfirmModal,
    closeReturnConfirmModal,
    submitReturn,
} from "@/store/slices/returnChatSlice";
import { useCustomerContext } from "@/context/CustomerContext";
import { useGuestContext } from "@/context/GuestContext";


interface ReturnChatbotProps {
    merchantId: string;
    orderId: string;
    productId: string;
    onComplete?: () => void;
}

const ReturnChatbot: React.FC<ReturnChatbotProps> = ({
    orderId,
    productId,
}) => {
    const navigate = useNavigate();
    const { user, loginType } = useAuth();
    const isGuest = loginType === 'token';
    const { refreshData } = useCustomerContext();
    const { fetchOnboardingData } = useGuestContext();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const chatContainerRef = useRef<HTMLDivElement>(null);
    const prevChatRef = useRef<ChatMessage[]>([]);

    // Refs for Custom Camera
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const dispatch = useDispatch<AppDispatch>();

    const MAX_FILES = 10;
    const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB

    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [optimisticMessage, setOptimisticMessage] = useState<{
        text?: string;
        files: { url: string; type: string }[];
    } | null>(null);

    /** Helper to strip Ref IDs from backend (e.g. [Ref: ...]) */
    const stripRefId = (text: any): string => {
        if (typeof text !== "string") return String(text || "");
        return text.replace(/\[Ref: [^\]]*\]/g, "").trim();
    };

    /** Centralized helper to add files with validation */
    const addFiles = (newFiles: File[]) => {
        setPendingFiles((prev) => {
            const combined = [...prev, ...newFiles];
            if (combined.length > MAX_FILES) {
                toast.error(`You can upload a maximum of ${MAX_FILES} files.`);
                return prev;
            }
            const totalSize = combined.reduce((sum, f) => sum + f.size, 0);
            if (totalSize > MAX_TOTAL_SIZE) {
                toast.error(`Total file size cannot exceed 20MB.`);
                return prev;
            }
            return combined;
        });
    };
    const [initError, setInitError] = useState<string | null>(null);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
    const [showAttachMenu, setShowAttachMenu] = useState(false);
    const [chatDragActive, setChatDragActive] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachMenuRef = useRef<HTMLDivElement>(null);

    const initializedRef = useRef(false);
    const triagedMessages = useRef<Set<string | number>>(new Set());

    const [triageReplies] = useState<Record<string, string>>(
        {},
    );
    const [triageLoading, setTriageLoading] = useState<Record<string, boolean>>(
        {},
    );

    const {
        currentChat,
        isLoading,
        isUploading,
        showSubmitReturn,
        returnId,
        orderId: reduxOrderId,
        productId: reduxProductId,
        message,
        setMessage,
        showConfirmSubmitModal,
        submitted,
        flowStep,
        handleCreateReturnRequest,
        handleSendReturnMessage,
        handleUploadReturnEvidence,
        handleSubmitReturn,
        handleClearReturnChat,
        handleEvaluateReturn,
        handleSetSelectedSolution,
        handleUpdateCustomerAddress,
        evaluationResult,
        customerAddress,
        isEvaluating,
        suggestedProducts,
        handleSetReplacementProductId,
        handleSetSelectedRefundMethod,
        handleSetFlowStep,
        isCancelled,
        supportRequested,
        showCancelConfirmation,
        handleSetSupportRequested,
        handlePushBotMessage,
        handleSetIsLoading,
        status,
    } = useReturnChat();

    // Initialize customer profile data into the chat state
    useEffect(() => {
        if (user && !customerAddress) {
            handleUpdateCustomerAddress({
                street: (typeof user.address === 'object' && user.address !== null) ? (user.address as any).street : (user.address || ""),
                email: user.email || ""
            });
        }
    }, [user, customerAddress, handleUpdateCustomerAddress]);

    const sendImageTriage = async (
        imageUrl: string,
        chat: ChatMessage[],
        returnIdForTriage: string,
        helperText?: string,
    ) => {
        if (isEvaluating || evaluationResult) return { success: false, message: "" };
        setTriageLoading((prev) => ({ ...prev, [imageUrl]: true }));
        try {
            const res = await axiosInstance.post("/api/triage/chat_image_for_return", {
                image_url: [imageUrl],
                return_id: returnIdForTriage,
                messagesArr: chat,
                helper_text: helperText,
            });
            const { success, message: triageMsgText, is_ready_for_resolution } = res.data;

            if (
                success &&
                typeof triageMsgText === "string" &&
                triageMsgText.trim()
            ) {
                // Push the bot message to Redux so it appears in the chat immediately
                handlePushBotMessage({ 
                    text: triageMsgText, 
                    msg_id: res.data.message_id,
                    is_ready_for_resolution: !!is_ready_for_resolution
                });
            }

            if (is_ready_for_resolution) {
                handleEvaluateReturn();
            }

            return { success, message: triageMsgText ?? "" };
        } catch (error) {
            console.error("Failed to send data to triage API:", error);
            return { success: false, message: "" };
        } finally {
            setTriageLoading((prev) => ({ ...prev, [imageUrl]: false }));
        }
    };

    // Cache for message timestamps
    const messageTimestamps = useRef<{ [key: number]: string }>({});

    // Consolidated Initialization: Handle cleanup and creation in one predictable flow
    useEffect(() => {
        const initReturnRequest = async () => {
            if (!orderId || !productId) return;

            // Check if we already have a valid session for THIS specific product to prevent double-firing
            // We use the values from the hook scope here; even if stale, the URL params [orderId, productId] 
            // are the primary drivers for a re-run.
            if (initializedRef.current || isLoading || (returnId && reduxOrderId === orderId && reduxProductId === productId)) {
                return;
            }

            // Mark as initializing immediately
            initializedRef.current = true;

            try {
                // If it's a DIFFERENT product than what's in Redux, clear state first
                if (reduxOrderId !== orderId || reduxProductId !== productId) {
                    handleClearReturnChat();
                }

                setInitError(null);
                await handleCreateReturnRequest({ order_id: orderId, product_id: productId });
            } catch (err) {
                console.error("Failed to create return request:", err);
                setInitError("Failed to start return request. Please try again.");
                initializedRef.current = false;
            }
        };

        void initReturnRequest();

        return () => {
            // No cleanup reset here to handle StrictMode.
        };
        // We strictly only re-run when the URL context changes. 
        // Including returnId/reduxOrderId in dependencies caused loops when those values updated.
    }, [orderId, productId, handleCreateReturnRequest, handleClearReturnChat]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [currentChat, triageReplies, triageLoading, optimisticMessage]);

    useEffect(() => {
        prevChatRef.current = currentChat;
    }, []);

    // Watch for new image uploads and send to triage
    useEffect(() => {
        if (!returnId || isEvaluating || evaluationResult) return;

        // Find user media messages that don't have a subsequent assistant message
        const untriagedUserMediaMsgs = currentChat.filter((msg, idx) => {
            const isUserMedia = msg.role === "user" && msg.content.some(c => c.type === "image_url" || c.type === "video_url");
            if (!isUserMedia) return false;

            // Has the AI responded to this or any subsequent message?
            const hasResponse = currentChat.slice(idx + 1).some(m => m.role === "assistant");
            return !hasResponse;
        });

        // To prevent multiple triggers for the same message, we can use the latest one
        if (untriagedUserMediaMsgs.length > 0) {
            const latestMsg = untriagedUserMediaMsgs[untriagedUserMediaMsgs.length - 1];
            const msgIndex = currentChat.indexOf(latestMsg);
            
            // Avoid duplicate triggers using a ref
            if (triagedMessages.current.has(msgIndex)) return;
            triagedMessages.current.add(msgIndex);

            // Extract content and trigger triage
            const textContent = latestMsg.content.find((c: any) => c.type === "text")?.text;
            const mediaContent = latestMsg.content.find(
                (c: any) =>
                    (c.type === "image_url" && c.image_url?.url) ||
                    (c.type === "video_url" && c.video_url?.url),
            );

            if (mediaContent && mediaContent.type === "image_url") {
                const fileUrl = mediaContent.image_url!.url;
                if (fileUrl) {
                    void sendImageTriage(fileUrl, currentChat, returnId, textContent);
                }
            } else if (mediaContent && mediaContent.type === "video_url") {
                // Video files notify text triage via a direct API call (no local echo)
                const videoUrl = mediaContent.video_url!.url;
                if (videoUrl) {
                    handleSetIsLoading(true);
                    void axiosInstance.post("/api/triage/chat_for_return", {
                        return_id: returnId,
                        messagesArr: [
                            ...currentChat,
                            {
                                role: "user",
                                content: [{ type: "text", text: `[System Note: User uploaded a video: ${videoUrl}]` }],
                                timestamp: new Date().toISOString()
                            }
                        ]
                    }).then(res => {
                        handleSetIsLoading(false);
                        if (res.data.success && res.data.message) {
                            handlePushBotMessage({
                                text: res.data.message,
                                msg_id: res.data.message_id,
                                is_ready_for_resolution: res.data.is_ready_for_resolution
                            });
                            if (res.data.is_ready_for_resolution) {
                                handleEvaluateReturn();
                            }
                        }
                    }).catch(err => {
                        handleSetIsLoading(false);
                        console.error("Failed to triage video:", err);
                    });
                }
            }
        }
    }, [currentChat, returnId, isEvaluating, evaluationResult]);

    // Capture timestamps for new messages
    useEffect(() => {
        const ts = new Date().toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        currentChat.forEach((_msg, idx) => {
            if (!(idx in messageTimestamps.current)) {
                messageTimestamps.current[idx] = ts;
            }
        });
    }, [currentChat]);

    // Stream to video element
    useEffect(() => {
        if (isCameraOpen && cameraStream && videoRef.current) {
            videoRef.current.srcObject = cameraStream;
            videoRef.current
                .play()
                .catch((e) => console.error("Error playing video stream:", e));
        }
    }, [isCameraOpen, cameraStream]);

    // --- CAMERA LOGIC ---
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false,
            });
            setCameraStream(stream);
            setIsCameraOpen(true);
        } catch (err) {
            console.error("Failed to access camera:", err);
            toast.error("Could not access camera. Please allow camera permissions.");
        }
    };

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach((track) => track.stop());
            setCameraStream(null);
        }
        setIsCameraOpen(false);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            const context = canvas.getContext("2d");
            if (context) {
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            const fileName = `return_capture_${Date.now()}.jpg`;
                            const file = new File([blob], fileName, { type: "image/jpeg" });
                            addFiles([file]);
                            stopCamera();
                        }
                    },
                    "image/jpeg",
                    0.9,
                );
            }
        }
    };

    const handleRemovePendingFile = (index: number) => {
        setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // --- GALLERY / FILE SELECT ---
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const validFiles = Array.from(files).filter((f) =>
                f.type.startsWith("image/") || f.type.startsWith("video/")
            );
            addFiles(validFiles);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // --- DRAG & DROP ON CHAT ---
    const handleChatDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setChatDragActive(true);
        } else if (e.type === "dragleave") {
            setChatDragActive(false);
        }
    };

    const handleChatDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setChatDragActive(false);
        const files = e.dataTransfer.files;
        if (files) {
            const validFiles = Array.from(files).filter((f) =>
                f.type.startsWith("image/") || f.type.startsWith("video/")
            );
            addFiles(validFiles);
        }
    };

    // Close attach menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (attachMenuRef.current && !attachMenuRef.current.contains(event.target as Node)) {
                setShowAttachMenu(false);
            }
        };
        if (showAttachMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showAttachMenu]);


    const handleMessageSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if ((!message.trim() && pendingFiles.length === 0) || !returnId) return;

        const currentMessageText = message.trim();

        try {
            if (pendingFiles.length > 0) {
                // Capturing previews for a unified optimistic UI bubble
                const previews = pendingFiles.map(f => ({
                    url: URL.createObjectURL(f),
                    type: f.type.startsWith("image/") ? "image_url" : "video_url"
                }));

                setOptimisticMessage({
                    text: currentMessageText,
                    files: previews
                });

                // Batch all files into a single upload
                const formData = new FormData();
                pendingFiles.forEach((file) => formData.append("files", file));
                formData.append("return_id", returnId);

                // If there's text, send it as helper_text with the images
                if (currentMessageText) {
                    formData.append("helper_text", currentMessageText);
                }

                // Clear inputs instantly
                setPendingFiles([]);
                setMessage("");

                try {
                    await handleUploadReturnEvidence({ formData, helperText: currentMessageText });
                    setOptimisticMessage(null);
                } catch (err) {
                    setOptimisticMessage(null);
                    throw err;
                }
            } else if (currentMessageText) {
                // Text-only message
                const newMessage: ChatMessage = {
                    role: "user",
                    content: [{ type: "text", text: currentMessageText }],
                    timestamp: new Date().toISOString(),
                };
                await handleSendReturnMessage(newMessage);
                setMessage("");
            }
        } catch (err) {
            console.error("Failed to send message/upload", err);
            toast.error("Failed to process request. Please try again.");
        }
    };

    const handleEvaluationTrigger = async () => {
        if (!returnId) return;
        handleEvaluateReturn();
    };

    // Effect: Automatically trigger evaluation when flowStep is 4
    // We use a ref to prevent double-triggering in StrictMode or during fast renders
    const evaluationTriggered = React.useRef(false);

    // Reset evaluation trigger if we go back to step 0
    useEffect(() => {
        if (flowStep === 0) {
            evaluationTriggered.current = false;
        }
    }, [flowStep]);

    useEffect(() => {
        if (flowStep === 4 && returnId && !isEvaluating && !evaluationResult && !evaluationTriggered.current) {
            evaluationTriggered.current = true;
            handleEvaluationTrigger();
        }
    }, [flowStep, returnId, isEvaluating, evaluationResult, handleEvaluationTrigger]);




    const handleConfirmedSubmit = async () => {
        dispatch(closeReturnConfirmModal());
        try {
            await handleSubmitReturn();
            // toast.success("Your return request has been submitted successfully!");
        } catch (err) {
            console.error(err);
        }
    };

    const TypingIndicator = () => (
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-1">
            <span
                className="tw-w-2 tw-h-2 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
                style={{ animationDelay: "0ms" }}
            />
            <span
                className="tw-w-2 tw-h-2 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
                style={{ animationDelay: "150ms" }}
            />
            <span
                className="tw-w-2 tw-h-2 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
                style={{ animationDelay: "300ms" }}
            />
        </div>
    );

    const formatTime = (msg?: ChatMessage, index?: number) => {
        if (msg?.timestamp) {
            return new Date(msg.timestamp).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
        }
        if (index !== undefined && index in messageTimestamps.current) {
            return messageTimestamps.current[index];
        }
        return new Date().toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    return (
        <div className="tw-flex tw-flex-col tw-h-[100dvh] tw-bg-[#F2F2F2]">
            {/* ─── Custom Camera Modal ─── */}
            <AnimatePresence>
                {isCameraOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="tw-fixed tw-inset-0 tw-z-[10000] tw-bg-black tw-flex tw-flex-col"
                    >
                        <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-z-10">
                            <span className="tw-text-white tw-font-medium">Take Photo</span>
                            <button
                                onClick={stopCamera}
                                className="tw-p-2 tw-bg-black/50 tw-rounded-full tw-text-white"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="tw-flex-1 tw-relative tw-flex tw-items-center tw-justify-center tw-bg-black tw-overflow-hidden">
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                className="tw-w-full tw-h-full tw-object-cover"
                            />
                            <canvas ref={canvasRef} className="tw-hidden" />
                        </div>

                        <div className="tw-bg-black tw-pb-10 tw-pt-6 tw-flex tw-items-center tw-justify-center tw-gap-8">
                            <button
                                onClick={capturePhoto}
                                className="tw-w-20 tw-h-20 tw-rounded-full tw-border-4 tw-border-white tw-flex tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white/20 tw-transition-colors"
                            >
                                <div className="tw-w-16 tw-h-16 tw-bg-white tw-rounded-full" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ─── Header ─── */}
            <header
                className="tw-bg-white tw-sticky tw-top-0 tw-z-50"
                style={{ boxShadow: "0 4px 6px -1px #353535" }}
            >
                <div className="tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-3 tw-max-w-4xl tw-mx-auto tw-relative">
                    <button
                        onClick={() => {
                            if (isGuest) {
                                fetchOnboardingData(orderId, true);
                            } else if (refreshData) {
                                refreshData(); // Background refresh
                            }
                            navigate(`/customer/orders/${orderId}`);
                        }}
                        className="tw-absolute tw-left-4 tw-p-2 tw-rounded-full hover:tw-bg-[#F2F2F2] tw-transition-colors tw-border-none tw-cursor-pointer"
                    >

                        <ChevronLeft size={24} className="tw-text-[#2a2a2a]" />
                    </button>

                    <img
                        src={ProtegaLogo}
                        alt="Protega"
                        className="tw-h-10 tw-object-contain"
                    />
                </div>
            </header>

            {/* ─── Chat Area ─── */}
            <main
                ref={chatContainerRef}
                className={`tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6 tw-relative ${chatDragActive ? "tw-ring-2 tw-ring-inset tw-ring-[#00c5fb]/40 tw-bg-[#00c5fb]/5" : ""
                    }`}
                onDragEnter={handleChatDrag}
                onDragLeave={handleChatDrag}
                onDragOver={handleChatDrag}
                onDrop={handleChatDrop}
            >
                {/* Drag overlay hint */}
                <AnimatePresence>
                    {chatDragActive && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-bg-white/90 tw-backdrop-blur-md tw-p-4"
                        >
                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-w-full tw-h-full tw-border-2 tw-border-dashed tw-border-[#00C5FB] tw-rounded-2xl">
                                <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center">
                                    <ImagePlus size={32} className="tw-text-[#00C5FB]" />
                                </div>
                                <p className="tw-text-[#2a2a2a] tw-font-semibold tw-text-base">Drop your images here</p>
                                <p className="tw-text-gray-400 tw-text-xs">Supports JPG, PNG, HEIC</p>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="tw-max-w-4xl tw-mx-auto">
                    {!returnId ? (
                        /* ─── Loading / Error State ─── */
                        initError ? (
                            <div className="tw-flex tw-flex-col tw-gap-5 tw-py-4">
                                {/* Anaya error bubble */}
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="tw-flex tw-items-end tw-gap-3"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-md">
                                        <img
                                            src={AnayaIcon}
                                            alt="Protega Assistant"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-max-w-[75%]">
                                        <div className="tw-bg-[#E8E8E8] tw-text-[#2a2a2a] tw-rounded-2xl tw-rounded-bl-sm tw-px-4 tw-py-3 tw-shadow-sm">
                                            <p className="tw-text-sm tw-leading-relaxed">
                                                Hi, I'm your Protega Assistant! We're currently experiencing some issues. Please try again later.
                                            </p>
                                        </div>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-ml-1">
                                            {formatTime()}
                                        </p>
                                    </div>
                                </motion.div>

                                {/* Go Back button */}
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2, delay: 0.15 }}
                                    className="tw-flex tw-justify-center tw-mt-4"
                                >
                                    <button
                                        onClick={() => {
                                            if (isGuest) {
                                                fetchOnboardingData(orderId, true);
                                            } else if (refreshData) {
                                                refreshData(); // Trigger in background
                                            }
                                            navigate(`/customer/orders/${orderId}`);
                                        }}
                                        className="tw-px-10 tw-py-3 tw-bg-[#00C5FB] tw-text-white tw-font-semibold tw-rounded-full tw-shadow-md hover:tw-bg-black tw-transition-colors tw-duration-500 tw-border-none tw-cursor-pointer tw-text-sm"
                                    >

                                        Go Back
                                    </button>
                                </motion.div>
                            </div>
                        ) : (
                            <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#F2F2F2]">
                                <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-[#00C5FB]" />
                            </div>
                        )
                    ) : (
                        /* ─── Messages ─── */
                        <div className="tw-flex tw-flex-col">
                            {currentChat.map((msg, index) => {
                                const isUser = msg.role === "user";
                                const isNextSameTarget = index < currentChat.length - 1 && currentChat[index + 1].role === msg.role;
                                const isPrevSameTarget = index > 0 && currentChat[index - 1].role === msg.role;
                                const isLastInGroup = !isNextSameTarget;

                                return (
                                    <motion.div
                                        key={index}
                                        layout
                                        initial={{ opacity: 0, x: isUser ? 20 : -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className={`tw-flex tw-items-end tw-gap-3 ${isUser ? "tw-flex-row-reverse" : ""} ${isPrevSameTarget ? "tw-mt-1" : "tw-mt-4"}`}
                                    >
                                        {!isUser && (
                                            <div className={`tw-w-9 tw-h-9 tw-shrink-0 ${isLastInGroup ? 'tw-shadow-md tw-rounded-full tw-overflow-hidden' : ''}`}>
                                                {isLastInGroup && (
                                                    <img
                                                        src={AnayaIcon}
                                                        alt="Protega Assistant"
                                                        className="tw-w-full tw-h-full tw-object-cover"
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {isUser && (
                                            <div className={`tw-w-9 tw-h-9 tw-shrink-0 ${isLastInGroup ? 'tw-shadow-md tw-rounded-full tw-overflow-hidden' : ''}`}>
                                                {isLastInGroup && (
                                                    <img
                                                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                                        alt="user"
                                                        className="tw-w-full tw-h-full tw-object-cover"
                                                        onError={(e) =>
                                                        ((e.target as HTMLImageElement).src =
                                                            `https://ui-avatars.com/api/?name=User&background=f8fafc&color=475569&bold=true`)
                                                        }
                                                    />
                                                )}
                                            </div>
                                        )}

                                        <div className="tw-max-w-[75%]">
                                            <div
                                                className={`tw-px-4 tw-py-3 tw-text-sm tw-leading-relaxed tw-shadow-sm tw-rounded-[24px] ${isUser
                                                    ? `tw-bg-[#D0D0D0] tw-text-[#2a2a2a] ${isLastInGroup ? 'tw-rounded-br-sm' : ''}`
                                                    : `tw-bg-[#E8E8E8] tw-text-[#2a2a2a] ${isLastInGroup ? 'tw-rounded-bl-sm' : ''}`
                                                    }`}
                                            >
                                                <div className="tw-flex tw-flex-col tw-gap-2 tw-w-fit">
                                                    {(Array.isArray(msg.content) ? msg.content : [{ type: "text", text: msg.content }]).map((content: any, i: number) => (
                                                        <div key={i} className="tw-w-fit tw-mr-2 first:tw-mt-0 tw-mt-2">
                                                            {content.type === "image_url" &&
                                                                content.image_url?.url && (
                                                                    <div className="tw-max-w-full tw-w-[240px] tw-aspect-square tw-rounded-[16px] tw-overflow-hidden tw-bg-[#f9fafb] tw-shadow-sm tw-border tw-border-gray-100 tw-transition-all">
                                                                        <img
                                                                            src={content.image_url.url}
                                                                            className="tw-w-full tw-h-full tw-object-cover"
                                                                            alt="evidence"
                                                                        />
                                                                    </div>
                                                                )}

                                                            {content.type === "video_url" &&
                                                                content.video_url?.url && (
                                                                    <div className="tw-w-[220px] tw-h-[220px] tw-rounded-xl tw-overflow-hidden tw-bg-gray-100 tw-shadow-inner">
                                                                        <video
                                                                            controls
                                                                            playsInline
                                                                            preload="metadata"
                                                                            controlsList="nodownload noremoteplayback noplaybackrate nofullscreen"
                                                                            src={content.video_url.url}
                                                                            className="tw-w-full tw-h-full tw-object-cover"
                                                                        />
                                                                    </div>
                                                                )}

                                                            {(content as any).type === "pdf_url" &&
                                                                (content as any).pdf_url?.url && (
                                                                    <a
                                                                        href={(content as any).pdf_url.url}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="tw-flex tw-items-center tw-gap-2 tw-bg-white/50 tw-p-3 tw-rounded-[16px] hover:tw-bg-white/80 tw-transition-colors"
                                                                    >
                                                                        <FileText
                                                                            size={18}
                                                                            className="tw-text-red-500 tw-shrink-0"
                                                                        />
                                                                        <span className="tw-truncate tw-text-sm tw-font-medium tw-text-[#2a2a2a]">
                                                                            {(content as any).pdf_url.file_name ||
                                                                                "View PDF"}
                                                                        </span>
                                                                    </a>
                                                                )}

                                                            {(content.type === "image_url" || content.type === "video_url" || content.type === "pdf_url") && content.helper_text && (
                                                                <p className="tw-text-sm tw-mt-2 tw-leading-relaxed">
                                                                    {stripRefId(content.helper_text)}
                                                                </p>
                                                            )}
                                                            {content.type === "text" && (
                                                                <p className="tw-text-sm tw-mt-2 tw-leading-relaxed">
                                                                    {stripRefId(content.text)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            {isLastInGroup && (
                                                <p
                                                    className={`tw-text-[10px] tw-text-gray-400 tw-mt-1 ${isUser ? "tw-text-right tw-mr-1" : "tw-ml-1"}`}
                                                >
                                                    {formatTime(msg, index)}
                                                </p>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}

                            {/* Unified Optimistic Message */}
                            {optimisticMessage && (
                                <motion.div
                                    layout
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-sm tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-max-w-[75%]">
                                        <div className="tw-bg-[#D0D0D0] tw-text-[#2a2a2a] tw-rounded-[24px] tw-rounded-br-sm tw-px-4 tw-py-3 tw-shadow-sm tw-w-fit">
                                            <div className="tw-flex tw-flex-col tw-gap-2 tw-w-fit">
                                                {optimisticMessage.files.map((preview, i) => (
                                                    <div key={i} className="tw-w-fit first:tw-mt-0 tw-mt-2">
                                                        <div className="tw-w-[220px] tw-h-[220px] tw-relative tw-rounded-[16px] tw-overflow-hidden tw-bg-gray-200/50">
                                                            {preview.type === "image_url" ? (
                                                                <img
                                                                    src={preview.url}
                                                                    className="tw-w-full tw-h-full tw-object-cover tw-opacity-50 tw-grayscale"
                                                                    alt="uploading"
                                                                />
                                                            ) : preview.type === "video_url" ? (
                                                                <video
                                                                    src={preview.url}
                                                                    className="tw-w-full tw-h-full tw-object-cover tw-opacity-30 tw-grayscale"
                                                                    muted
                                                                    playsInline
                                                                />
                                                            ) : (
                                                                <div className="tw-w-full tw-h-full tw-bg-gray-300 tw-flex tw-items-center tw-justify-center">
                                                                    <FileText size={40} className="tw-text-gray-500" />
                                                                </div>
                                                            )}
                                                            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
                                                                <div className="tw-w-8 tw-h-8 tw-border-2 tw-border-[#00C5FB] tw-border-t-transparent tw-rounded-full tw-animate-spin" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                                {optimisticMessage.text && (
                                                    <p className="tw-text-sm tw-leading-relaxed tw-mt-2">
                                                        {stripRefId(optimisticMessage.text)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-text-right tw-mr-1">
                                            {isUploading ? "Uploading..." : "Processing..."}
                                        </p>
                                    </div>
                                </motion.div>
                            )}

                            {/* AI Loading Indicator (Typing Dots) */}
                            { (isLoading || Object.values(triageLoading).some(Boolean)) && (
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-mt-4"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-md">
                                        <img
                                            src={AnayaIcon}
                                            alt="Protega Assistant"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#E8E8E8] tw-rounded-[24px] tw-rounded-bl-sm tw-px-4 tw-py-4 tw-shadow-sm">
                                        <TypingIndicator />
                                    </div>
                                </motion.div>
                            )}


                            <AnimatePresence>
                                {submitted && status !== "approved" && (isCancelled || (flowStep !== 6 && flowStep !== 10) || (supportRequested && !isLoading)) && (!supportRequested || (supportRequested && !isLoading)) && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2, delay: 0.15 }}
                                        className="tw-flex tw-justify-center tw-mt-4"
                                    >
                                        <button
                                            onClick={() => {
                                                if (isGuest) {
                                                    fetchOnboardingData(orderId, true);
                                                } else if (refreshData) {
                                                    refreshData(); // Background refresh
                                                }
                                                navigate(`/customer/orders/${orderId}`);
                                            }}
                                            className="tw-px-10 tw-py-3 tw-bg-[#00C5FB] tw-text-white tw-font-semibold tw-rounded-full tw-shadow-md hover:tw-bg-black tw-transition-colors tw-duration-500 tw-border-none tw-cursor-pointer tw-text-sm"
                                        >
                                            Back to Orders
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Resolution Quick Replies for Approved Returns */}
                            <AnimatePresence>
                                {submitted && status === "approved" && !isLoading && evaluationResult?.category && (
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-8"
                                    >
                                        <div className="tw-w-9 tw-h-9 tw-shrink-0 tw-shadow-sm tw-rounded-full tw-overflow-hidden tw-border tw-border-gray-100">
                                            <img
                                                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                                alt="user"
                                                className="tw-w-full tw-h-full tw-object-cover"
                                            />
                                        </div>
                                        <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[260px] tw-flex tw-flex-col tw-gap-2.5">
                                            <p className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-px-1">Finalize Resolution</p>
                                            <button
                                                onClick={async () => {
                                                    // Force a refresh of the onboarding data to capture the latest AI-evaluated reason/category
                                                    if (isGuest) {
                                                        await fetchOnboardingData(orderId, true);
                                                    } else if (refreshData) {
                                                        refreshData(); // Background refresh
                                                    }

                                                    const category = evaluationResult.category.toLowerCase();
                                                    let targetRoute = "resolve-fix"; // default

                                                    if (category.includes("fix")) {
                                                        targetRoute = "resolve-fix";
                                                    } else if (category.includes("demo") || category.includes("damage")) {
                                                        targetRoute = "resolve-damage";
                                                    } else if (category.includes("preference")) {
                                                        targetRoute = "resolve-preference";
                                                    }

                                                    navigate(`/customer/orders/${orderId}/${targetRoute}?productId=${productId}`);
                                                }}
                                                className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-[#00C5FB] tw-text-white hover:tw-bg-black tw-transition-all tw-border-none tw-shadow-lg tw-shadow-[#00C5FB]/20"
                                            >
                                                Proceed with resolution
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (isGuest) {
                                                        fetchOnboardingData(orderId, true);
                                                    } else if (refreshData) {
                                                        refreshData(); // Background refresh
                                                    }
                                                    navigate(`/customer/orders/${orderId}`);
                                                }}
                                                className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-white tw-text-[#2a2a2a] tw-border-2 tw-border-gray-100 hover:tw-border-gray-300 tw-transition-all"
                                            >
                                                Back to Orders
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Quick Solution Selection (User Aligned) */}
                            {flowStep === 5 && evaluationResult && !isLoading && (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-4"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-md tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[260px] tw-flex tw-flex-col tw-gap-2.5">
                                        <p className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-px-1">Select a Solution</p>
                                        {evaluationResult.solutions?.map((sol: string, idx: number) => (
                                            <button
                                                key={idx}
                                                onClick={async () => {
                                                    const solutionMap: Record<string, string> = {
                                                        "Same Replacement": "exchange_same",
                                                        "Store Credit": "store_credit",
                                                        "Original Refund": "refund"
                                                    };
                                                    const snakeSol = solutionMap[sol] || sol.toLowerCase().replace(/ /g, '_');
                                                    handleSetSelectedSolution(snakeSol);
                                                    await handleSendReturnMessage({
                                                        role: "user",
                                                        content: [{ type: "text", text: sol }],
                                                        timestamp: new Date().toISOString(),
                                                    });
                                                }}
                                                className="tw-w-full tw-py-3 tw-px-6 tw-text-[13px] tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-transition-all tw-duration-300 tw-border-none tw-bg-[#00C5FB] tw-text-white hover:tw-bg-black hover:tw-scale-[1.02] tw-shadow-lg tw-shadow-[#00C5FB]/20"
                                            >
                                                {stripRefId(sol)}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {flowStep === 6 && status !== "approved" && !isCancelled && !supportRequested && (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-4"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-shrink-0 tw-shadow-sm tw-rounded-full tw-overflow-hidden tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[240px] tw-flex tw-flex-col tw-gap-2">
                                        <button
                                            onClick={async () => {
                                                if (isGuest) {
                                                    fetchOnboardingData(orderId, true);
                                                } else if (refreshData) {
                                                    refreshData(); // Background refresh
                                                }
                                                navigate(`/customer/orders/${orderId}`);
                                            }}
                                            className="tw-w-full tw-py-2.5 tw-bg-white tw-text-[#2a2a2a] tw-text-sm tw-font-bold tw-rounded-2xl tw-border-2 tw-border-gray-100 hover:tw-border-gray-300 tw-transition-all tw-cursor-pointer tw-outline-none"
                                        >
                                            Back to Orders
                                        </button>
                                        <button
                                            onClick={async () => {
                                                handleSetSupportRequested(true);
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "I would like to contact support regarding my request." }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                                if (returnId) {
                                                    await dispatch(submitReturn({ status: "support_requested" }));
                                                }
                                                // Removed window.open mailto to stay in chat flow as requested
                                            }}
                                            className="tw-w-full tw-py-2.5 tw-bg-red-600 tw-text-white tw-text-sm tw-font-bold tw-rounded-2xl hover:tw-bg-red-700 tw-transition-all tw-border-none tw-cursor-pointer tw-outline-none tw-shadow-lg tw-shadow-red-600/10"
                                        >
                                            Contact Support
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {flowStep === 10 && status !== "approved" && !isCancelled && !supportRequested && (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-4"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-shrink-0 tw-shadow-sm tw-rounded-full tw-overflow-hidden tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[240px] tw-flex tw-flex-col tw-gap-2">
                                        <button
                                            onClick={async () => {
                                                if (isGuest) {
                                                    fetchOnboardingData(orderId, true);
                                                } else if (refreshData) {
                                                    refreshData(); // Background refresh
                                                }
                                                navigate(`/customer/orders/${orderId}`);
                                            }}
                                            className="tw-w-full tw-py-2.5 tw-bg-white tw-text-[#2a2a2a] tw-text-sm tw-font-bold tw-rounded-2xl tw-border-2 tw-border-gray-100 hover:tw-border-gray-300 tw-transition-all tw-cursor-pointer tw-outline-none"
                                        >
                                            Back to Orders
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {/* Step 11: Out-of-Stock Inquiry */}
                            {flowStep === 11 && !isLoading && (
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-4"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-shrink-0 tw-shadow-sm tw-rounded-full tw-overflow-hidden tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[240px] tw-flex tw-flex-col tw-gap-2.5">
                                        <p className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-px-1">Explore Options</p>
                                        <button
                                            onClick={async () => {
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "Yes, show options" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-[#00C5FB] tw-text-white hover:tw-bg-black tw-transition-all tw-border-none tw-shadow-lg tw-shadow-[#00C5FB]/20"
                                        >
                                            Yes, show options
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "No, I'll take a refund" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-white tw-text-[#2a2a2a] tw-border-2 tw-border-gray-100 hover:tw-border-gray-300 tw-transition-all"
                                        >
                                            No, take refund
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {flowStep === 7 && !isLoading && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="tw-flex tw-gap-4 tw-overflow-x-auto tw-pb-6 tw-pl-[48px] tw-pr-4 tw-snap-x tw-snap-mandatory no-scrollbar"
                                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                >
                                    {(suggestedProducts || []).map((prod: any, idx: number) => (
                                        <div
                                            key={prod.id || idx}
                                            className="tw-group tw-w-[170px] tw-bg-[#2a2a2a] tw-rounded-[24px] tw-p-3 tw-shadow-xl tw-snap-start tw-shrink-0 tw-transition-all tw-duration-500 hover:tw-scale-[1.02]"
                                        >
                                            <div className="tw-w-full tw-aspect-square tw-rounded-[18px] tw-overflow-hidden tw-bg-white tw-flex tw-items-center tw-justify-center tw-mb-4">
                                                <img
                                                    src={prod.image_url}
                                                    alt={prod.title}
                                                    className="tw-w-[90%] tw-h-[90%] tw-object-contain tw-transition-transform tw-duration-700 group-hover:tw-scale-110"
                                                />
                                            </div>

                                            <div className="tw-flex tw-flex-col tw-gap-2">
                                                <p className="tw-text-white tw-font-bold tw-text-[12px] tw-leading-tight tw-line-clamp-2">
                                                    {prod.title}
                                                </p>
                                                <p className="tw-text-white tw-font-bold tw-text-[15px]">
                                                    ₹{prod.price?.toLocaleString('en-IN')}
                                                </p>
                                                <button
                                                    onClick={async () => {
                                                        handleSetReplacementProductId(prod.id);
                                                        await handleSendReturnMessage({
                                                            role: "user",
                                                            content: [{ type: "text", text: `I'll take the ${prod.title}` }],
                                                            timestamp: new Date().toISOString(),
                                                        });
                                                        handleSetSelectedSolution("exchange_different");
                                                        handleSetFlowStep(8);
                                                    }}
                                                    className="tw-w-fit tw-bg-white tw-text-black tw-px-4 tw-py-2 tw-rounded-full tw-text-[11px] tw-font-black hover:tw-bg-[#00C5FB] hover:tw-text-white tw-transition-all tw-duration-300 tw-border-none tw-cursor-pointer tw-flex tw-items-center tw-gap-1"
                                                >
                                                    <span className="tw-text-xs">+</span> Take
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                            {flowStep === 12 && !isLoading && (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-4"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-shrink-0 tw-shadow-sm tw-rounded-full tw-overflow-hidden tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[260px] tw-flex tw-flex-col tw-gap-2.5">
                                        <p className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-px-1">Refund Method</p>
                                        <button
                                            onClick={async () => {
                                                handleSetSelectedRefundMethod("Original Payment Source");
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "Original Payment Source" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                                await handleSubmitReturn();
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-[#00C5FB] tw-text-white hover:tw-bg-black tw-transition-all tw-border-none tw-shadow-lg tw-shadow-[#00C5FB]/20"
                                        >
                                            Original Payment Source
                                        </button>
                                        <button
                                            onClick={async () => {
                                                handleSetSelectedRefundMethod("Store Credit");
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "Store Credit" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                                await handleSubmitReturn();
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-white tw-text-[#2a2a2a] tw-border-2 tw-border-gray-100 hover:tw-border-gray-300 tw-transition-all"
                                        >
                                            Store Credit
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {flowStep === 8 && !isLoading && (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-4"
                                >
                                    {/* Quick Reply Options (User Aligned) */}
                                    <div className="tw-w-9 tw-h-9 tw-shrink-0 tw-shadow-sm tw-rounded-full tw-overflow-hidden tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[240px] tw-flex tw-flex-col tw-gap-2.5">
                                        <p className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-px-1">Verify Address</p>
                                        <button
                                            onClick={async () => {
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "Details are correct" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                                await handleSubmitReturn();
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-[#00C5FB] tw-text-white hover:tw-bg-black tw-transition-all tw-border-none tw-shadow-lg tw-shadow-[#00C5FB]/20"
                                        >
                                            Details are correct
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "Edit Address" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-white tw-text-[#2a2a2a] tw-border-2 tw-border-gray-100 hover:tw-border-gray-300 tw-transition-all"
                                        >
                                            Edit Address
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {showCancelConfirmation && !isLoading && (
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse tw-mt-4 tw-px-4 tw-mb-4"
                                >
                                    <div className="tw-w-9 tw-h-9 tw-shrink-0 tw-shadow-sm tw-rounded-full tw-overflow-hidden tw-border tw-border-gray-100">
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=f8fafc&color=475569&bold=true`}
                                            alt="user"
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    </div>
                                    <div className="tw-bg-[#D0D0D0] tw-rounded-[24px] tw-rounded-br-sm tw-p-4 tw-shadow-sm tw-w-[240px] tw-flex tw-flex-col tw-gap-2.5">
                                        <p className="tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-px-1">Confirm Cancellation</p>
                                        <button
                                            onClick={async () => {
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "Yes, I want to cancel" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-red-600 tw-text-white hover:tw-bg-red-700 tw-transition-all tw-border-none tw-shadow-lg tw-shadow-red-600/10"
                                        >
                                            Yes, Cancel Request
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await handleSendReturnMessage({
                                                    role: "user",
                                                    content: [{ type: "text", text: "No, continue with return" }],
                                                    timestamp: new Date().toISOString(),
                                                });
                                            }}
                                            className="tw-w-full tw-py-3 tw-px-4 tw-text-xs tw-font-bold tw-rounded-2xl tw-cursor-pointer tw-bg-white tw-text-[#2a2a2a] tw-border-2 tw-border-gray-100 hover:tw-border-gray-300 tw-transition-all"
                                        >
                                            No, keep going
                                        </button>
                                    </div>
                                </motion.div>
                            )}


                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>
            </main>

            {/* ─── Submit Return Button ─── */}
            <AnimatePresence>
                {false && showSubmitReturn && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="tw-px-4 tw-pb-2"
                    >
                        <div className="tw-max-w-4xl tw-mx-auto">
                            <button
                                onClick={() => dispatch(openReturnConfirmModal())}
                                disabled={isLoading}
                                className="tw-w-full tw-py-3.5 tw-bg-[#00C5FB] tw-text-[#2a2a2a] tw-font-semibold tw-rounded-xl tw-shadow-lg hover:tw-bg-[#34d3ff] tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                Submit Return Request
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ─── Input Area or Choice Buttons ─── */}
            {/* ─── Persistent Message Input Footer ─── */}
            <>
                <footer className="tw-bg-white tw-border-t tw-border-gray-200 tw-p-4 tw-sticky tw-bottom-0">
                    <div className="tw-max-w-4xl tw-mx-auto">
                        <form onSubmit={handleMessageSubmit}>
                            {/* Hidden file input for gallery */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,video/*"
                                multiple
                                onChange={handleFileSelect}
                                className="tw-hidden"
                            />

                            <div className={`tw-bg-[#F2F2F2] tw-border tw-border-gray-200 focus-within:tw-border-[#00c5fb] focus-within:tw-ring-2 focus-within:tw-ring-[#00c5fb]/20 tw-transition-all ${pendingFiles.length > 0 ? "tw-rounded-2xl tw-p-3" : "tw-rounded-full tw-p-1.5 tw-pl-2"}`}>
                                {/* Pending Files Preview */}
                                {pendingFiles.length > 0 && (
                                    <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-3 tw-mb-2">
                                        {pendingFiles.map((file, idx) => (
                                            <div key={idx} className="tw-relative tw-shrink-0 tw-group">
                                                <div className="tw-w-[100px] tw-h-[100px] tw-rounded-xl tw-overflow-hidden tw-ring-1 tw-ring-black/10 tw-bg-gray-100">
                                                    {file.type.startsWith("video/") ? (
                                                        <video
                                                            src={URL.createObjectURL(file)}
                                                            className="tw-w-full tw-h-full tw-object-cover"
                                                            muted
                                                            playsInline
                                                        />
                                                    ) : (
                                                        <img
                                                            src={URL.createObjectURL(file)}
                                                            alt="preview"
                                                            className="tw-w-full tw-h-full tw-object-cover"
                                                        />
                                                    )}
                                                    {file.type.startsWith("video/") && (
                                                        <div className="tw-absolute tw-bottom-1 tw-left-1 tw-bg-black/60 tw-text-white tw-text-[8px] tw-px-1.5 tw-py-0.5 tw-rounded-md tw-font-bold">
                                                            VIDEO
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePendingFile(idx)}
                                                    className="tw-absolute tw-top-1 tw-right-1 tw-w-6 tw-h-6 tw-bg-black/60 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-none tw-cursor-pointer hover:tw-bg-black/80 tw-transition-colors tw-backdrop-blur-sm"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Input Row */}
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <div className="tw-relative" ref={attachMenuRef}>
                                        <button
                                            type="button"
                                            onClick={() => setShowAttachMenu((v) => !v)}
                                            disabled={!returnId || isLoading || isUploading || !!initError || submitted || status === 'manual_review' || status === 'rejected' || status === 'support_requested'}
                                            className="tw-p-2.5 tw-rounded-full tw-cursor-pointer hover:tw-bg-[#D3D3D3] tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                                        >
                                            <Paperclip size={20} className="tw-text-[#2a2a2a]" />
                                        </button>

                                        <AnimatePresence>
                                            {showAttachMenu && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 8, scale: 0.95 }}
                                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                                    exit={{ opacity: 0, y: 8, scale: 0.95 }}
                                                    transition={{ duration: 0.15 }}
                                                    className="tw-absolute tw-bottom-full tw-left-0 tw-mb-3 tw-bg-white tw-rounded-[24px] tw-overflow-hidden tw-w-[220px] tw-z-50"
                                                    style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)' }}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowAttachMenu(false);
                                                            startCamera();
                                                        }}
                                                        className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-[#2a2a2a] tw-bg-white hover:tw-bg-[#F2F2F2] tw-transition-colors tw-border-none tw-cursor-pointer tw-outline-none"
                                                    >
                                                        <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                                                            <Camera size={18} className="tw-text-[#00C5FB]" />
                                                        </div>
                                                        <span className="tw-text-sm tw-font-medium tw-whitespace-nowrap">Take Photo</span>
                                                    </button>
                                                    <div className="tw-mx-4 tw-border-t tw-border-gray-100" />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowAttachMenu(false);
                                                            fileInputRef.current?.click();
                                                        }}
                                                        className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-[#2a2a2a] tw-bg-white hover:tw-bg-[#F2F2F2] tw-transition-colors tw-border-none tw-cursor-pointer tw-outline-none"
                                                    >
                                                        <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                                                            <ImagePlus size={18} className="tw-text-[#00C5FB]" />
                                                        </div>
                                                        <span className="tw-text-sm tw-font-medium tw-whitespace-nowrap">Choose from Gallery</span>
                                                    </button>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    <input
                                        type="text"
                                        value={message}
                                        disabled={!returnId || isLoading || isUploading || !!initError || submitted || status === 'manual_review' || status === 'rejected' || status === 'support_requested'}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder={
                                            initError
                                                ? "Currently unavailable..."
                                                : (submitted || status === 'manual_review' || status === 'rejected' || status === 'support_requested')
                                                    ? "Chat has been concluded"
                                                    : returnId
                                                        ? "Type your message..."
                                                        : "Setting up your return..."
                                        }
                                        className="tw-flex-1 tw-bg-transparent tw-text-[#2a2a2a] tw-text-sm tw-outline-none tw-py-2 tw-placeholder-gray-400 disabled:tw-cursor-not-allowed"
                                    />

                                    <button
                                        type="submit"
                                        disabled={
                                            (!message.trim() && pendingFiles.length === 0) ||
                                            !returnId ||
                                            isLoading ||
                                            isUploading ||
                                            !!initError ||
                                            submitted ||
                                            status === 'manual_review' ||
                                            status === 'rejected' ||
                                            status === 'support_requested'
                                        }
                                        className="tw-p-2.5 tw-bg-[#00C5FB] tw-rounded-full tw-text-[#2a2a2a] tw-shadow-md hover:tw-bg-[#34d3ff] tw-transition-all disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:tw-shadow-none"
                                    >
                                        <Send size={18} />
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </footer>
            </>


            {/* ─── Confirm Submit Modal ─── */}


            {/* ─── Confirm Submit Modal ─── */}
            <AnimatePresence>
                {showConfirmSubmitModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-backdrop-blur-sm tw-p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="tw-bg-white tw-rounded-[24px] tw-w-full tw-max-w-md tw-p-6 tw-shadow-2xl"
                        >
                            <div className="tw-w-16 tw-h-16 tw-bg-[#00c5fb]/20 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                                <RotateCcw size={32} className="tw-text-[#00C5FB]" />
                            </div>

                            <h2 className="tw-text-xl tw-font-bold tw-text-[#2a2a2a] tw-text-center tw-mb-2">
                                Confirm return details
                            </h2>

                            <p className="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-6 tw-leading-relaxed">
                                Before submitting your return request, please check that you've
                                described the reason for return and uploaded product photos.
                                <br />
                                <br />
                                <span className="tw-text-amber-600 tw-font-medium">
                                    Returns submitted without sufficient information may be
                                    delayed.
                                </span>
                            </p>

                            <button
                                onClick={async () => {
                                    if (returnId) {
                                        await handleSendReturnMessage({
                                            role: "user",
                                            content: [{ type: "text", text: "Please submit my request." }],
                                            timestamp: new Date().toISOString(),
                                        });
                                    }
                                    handleConfirmedSubmit();
                                }}
                                className="tw-w-full tw-py-3.5 tw-bg-[#00C5FB] tw-text-[#2a2a2a] tw-font-semibold tw-rounded-full tw-shadow-lg hover:tw-bg-[#34d3ff] tw-transition-all tw-mb-3"
                            >
                                Yes, submit my return
                            </button>

                            <button
                                onClick={async () => {
                                    dispatch(closeReturnConfirmModal());
                                    if (returnId) {
                                        await handleSendReturnMessage({
                                            role: "user",
                                            content: [{ type: "text", text: "I need to add more details." }],
                                            timestamp: new Date().toISOString(),
                                        });
                                    }
                                }}
                                className="tw-w-full tw-py-3.5 tw-bg-[#F2F2F2] tw-text-[#2a2a2a] tw-font-semibold tw-rounded-full hover:tw-bg-[#D3D3D3] tw-transition-colors"
                            >
                                Review & add details
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ReturnChatbot;
